import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Country {
  code: string;
  name: string;
}

interface CountrySelectProps {
  value: string | null;
  onChange: (code: string | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

// Shared cache across all instances
let countriesCache: Country[] | null = null;

export function CountrySelect({
  value,
  onChange,
  placeholder = "Select country",
  className,
  disabled,
}: CountrySelectProps) {
  const [countries, setCountries] = useState<Country[]>(countriesCache ?? []);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (countriesCache) return;
    supabase
      .from("countries")
      .select("code, name")
      .order("name")
      .then(({ data }) => {
        const list = (data as Country[]) ?? [];
        countriesCache = list;
        setCountries(list);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [countries, search]);

  const selected = countries.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal bg-background border-border",
            !selected && "text-muted-foreground",
            className
          )}
        >
          {selected ? selected.name : placeholder}
          <div className="flex items-center gap-1 ml-2">
            {selected && (
              <X
                className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b border-border">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search countries…"
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No countries found.</p>
          ) : (
            filtered.map((country) => (
              <button
                key={country.code}
                onClick={() => {
                  onChange(country.code === value ? null : country.code);
                  setOpen(false);
                  setSearch("");
                }}
                className={cn(
                  "flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm text-left hover:bg-accent transition-colors",
                  country.code === value && "bg-accent"
                )}
              >
                <Check
                  className={cn(
                    "h-3.5 w-3.5 flex-shrink-0",
                    country.code === value ? "opacity-100 text-primary" : "opacity-0"
                  )}
                />
                <span className="flex-1 truncate">{country.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{country.code}</span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Shared country list (same table and cache as CountrySelect). */
export function useCountries(): Country[] {
  const [countries, setCountries] = useState<Country[]>(countriesCache ?? []);
  useEffect(() => {
    if (countriesCache) return;
    supabase.from("countries").select("code, name").order("name").then(({ data }) => {
      const list = (data as Country[]) ?? [];
      countriesCache = list;
      setCountries(list);
    });
  }, []);
  return countries;
}

/** Helper: resolve a country code to its name (for display). Uses cache. */
export function useCountryName(code: string | null): string | null {
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    if (!code) { setName(null); return; }
    if (countriesCache) {
      setName(countriesCache.find((c) => c.code === code)?.name ?? code);
      return;
    }
    supabase
      .from("countries")
      .select("name")
      .eq("code", code)
      .single()
      .then(({ data }) => setName(data?.name ?? code));
  }, [code]);

  return name;
}

/** Inline component to render a country name from a code */
export function CountryName({ code, className }: { code: string | null; className?: string }) {
  const name = useCountryName(code);
  if (!name) return null;
  return <span className={className}>{name}</span>;
}
